/**
 * Custom Cart Drawer Component (Section Version)
 * Handles dynamic offer thresholds, progress bar, and cart operations.
 */
class CustomCartDrawer extends HTMLElement {
    constructor() {
        super();
        this.classes = {
            active: 'active',
            loading: 'is-loading'
        };
        this.removedVariantIds = new Set(); // Track user-removed gifts
        this.lastUnlockedCount = 0; // Track milestones for confetti
        this.firstLoad = true; // Supress confetti on initial load
    }

    connectedCallback() {
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleGlobalSubmit = this.handleGlobalSubmit.bind(this);
        this.changeQty = this.changeQty.bind(this);

        document.addEventListener('custom-cart:open', this.open);
        document.addEventListener('cart:item-added', () => { this.fetchCart(); this.open(); });
        document.addEventListener('cart:updated', () => this.fetchCart());
        document.addEventListener('submit', this.handleGlobalSubmit);

        // Force init on load
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
            this.init();
        }
    }

    disconnectedCallback() {
        document.removeEventListener('custom-cart:open', this.open);
        document.removeEventListener('submit', this.handleGlobalSubmit);
        this.removeEventListener('click', this.handleClick);
    }

    init() {
        this.drawer = this.querySelector('.ccd-drawer');
        this.overlay = this.querySelector('.ccd-overlay');
        this.progressBar = this.querySelector('.ccd-progress-fill');
        this.progressText = this.querySelector('.ccd-progress-text');
        this.itemsContainer = this.querySelector('.ccd-items-wrapper');
        this.milestoneLayer = this.querySelector('.ccd-milestones-layer');

        this.offers = this.getOffers();
        console.log('CCD: Offers loaded:', this.offers);
        if (this.offers.length > 0) {
            // Ensure offers are sorted ascendingly
            this.offers.sort((a, b) => a.threshold - b.threshold);
            this.renderMilestones();
        } else {
            // Retry if empty
            setTimeout(() => {
                this.offers = this.getOffers();
                if (this.offers.length > 0) {
                    this.offers.sort((a, b) => a.threshold - b.threshold);
                    this.renderMilestones();
                    if (this.lastCart) this.updateProgress(this.lastCart);
                }
            }, 500);
        }

        this.removeEventListener('click', this.handleClick);
        this.addEventListener('click', this.handleClick);

        // Initial fetch
        this.fetchCart();

        // Init Coupons
        this.initCoupons();

        // Bind Footer Events (Checkout, etc)
        this.bindFooterEvents();
    }

    bindFooterEvents() {
        const checkoutBtn = this.querySelector('.ccd-checkout-btn');
        if (checkoutBtn) {
            checkoutBtn.addEventListener('click', (e) => {
                const activeCode = sessionStorage.getItem('ccd_discount_code');
                if (activeCode) {
                    e.preventDefault();
                    window.location.href = `/checkout?discount=${activeCode}`;
                }
            });
        }
    }

    getOffers() {
        try {
            const script = this.querySelector('#ccd-offers-data');
            if (!script) return [];
            return JSON.parse(script.textContent);
        } catch (e) {
            console.error('CCD: Error parsing offers', e, script ? script.textContent : 'No Script Tag');
            return [];
        }
    }

    renderMilestones() {
        if (!this.milestoneLayer || !this.offers.length) return;
        this.milestoneLayer.innerHTML = ''; // Clear existing

        const count = this.offers.length;
        const widthPerBlock = 100 / count;

        this.offers.forEach((offer, index) => {
            const percent = (index + 1) * widthPerBlock; // 1-based index for spacing (e.g., 50%, 100%)
            const el = document.createElement('div');
            el.className = 'ccd-milestone';
            el.style.left = `${percent}%`;
            el.dataset.threshold = offer.threshold;

            // Format money simply (approximate)
            const moneyVal = 'Rs. ' + (offer.threshold / 100).toLocaleString();

            let iconHtml = '';
            // Check if icon is valid (Shopify sometimes returns empty string or no-image URL if empty)
            if (offer.icon && !offer.icon.includes('no-image')) {
                iconHtml = `<img src="${offer.icon}" class="icon-default" style="width:100%; height:100%; object-fit:contain; border-radius:50%;" alt="Reward">`;
            } else {
                iconHtml = `<svg class="icon-default" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>`;
            }

            el.innerHTML = `
                  <div class="ccd-milestone-label-top">${offer.rewardText}</div>
                  <div class="ccd-milestone-icon">
                     ${iconHtml}
                     <svg class="icon-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" style="display:none;"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </div>
                  <div class="ccd-milestone-label-bottom">${moneyVal}</div>
            `;
            this.milestoneLayer.appendChild(el);
        });
    }

    handleClick(e) {
        if (e.target.closest('.ccd-close-btn') || e.target.closest('.ccd-overlay')) {
            this.close();
        }

        const removeBtn = e.target.closest('.ccd-remove-btn');
        if (removeBtn) {
            e.preventDefault();
            this.changeQty(removeBtn.dataset.line, 0);
        }
    }

    async handleGlobalSubmit(e) {
        if (e.target.action && e.target.action.includes('/cart/add')) {
            e.preventDefault();
            const submitBtn = e.target.querySelector('[type="submit"]');
            const originalText = submitBtn ? submitBtn.innerText : '';
            if (submitBtn) { submitBtn.setAttribute('disabled', 'true'); submitBtn.innerText = 'Adding...'; }
            const formData = new FormData(e.target);
            try {
                await fetch('/cart/add.js', { method: 'POST', body: formData });
                await this.fetchCart();
                this.open();
            } catch (err) { console.error('CCD: Add to cart failed', err); }
            finally { if (submitBtn) { submitBtn.removeAttribute('disabled'); submitBtn.innerText = originalText; } }
        }
    }

    open() {
        if (this.drawer) {
            this.classList.add(this.classes.active);
            document.body.style.overflow = 'hidden';
        } else {
            console.warn('CCD: Drawer not found, initializing...');
            this.init();
            // Force reflow/check
            if (this.querySelector('.ccd-drawer')) {
                this.classList.add(this.classes.active);
                document.body.style.overflow = 'hidden';
            }
        }
    }

    close() {
        this.classList.remove(this.classes.active);
        document.body.style.overflow = '';
    }

    async fetchCart() {
        try {
            const res = await fetch('/cart.js');
            const cart = await res.json();
            this.lastCart = cart;

            try {
                const sectionUrl = `/?section_id=${this.dataset.sectionId}`;
                const sectionRes = await fetch(sectionUrl);
                const sectionText = await sectionRes.text();

                const doc = new DOMParser().parseFromString(sectionText, 'text/html');
                const newItems = doc.querySelector('.ccd-items-wrapper');
                if (newItems && this.itemsContainer) this.itemsContainer.innerHTML = newItems.innerHTML;

                const newFooter = doc.querySelector('.ccd-footer');
                const currentFooter = this.querySelector('.ccd-footer');
                if (newFooter && currentFooter) {
                    currentFooter.innerHTML = newFooter.innerHTML;
                    this.bindFooterEvents(); // Re-bind events for new elements
                }

                // Re-init Coupons to update count and bind events
                this.initCoupons();

                // IMPORTANT: Re-init to grab new elements AND re-render milestones logic if needed
                // But avoid full re-render of milestones if they are static. 
                // However, updated items logic might have changed structure.
                // We mainly need to re-query the dynamic parts.
                // this.init(); // <-- Avoid full re-init loops. Just re-query dynamic parts.

                // Only re-bind dynamic elements inside items/footer
                // Milestones are outside items wrapper, so they persist.

            } catch (htmlErr) { console.warn('CCD: HTML fetch error', htmlErr); }

            this.updateProgress(cart);

        } catch (err) { console.error('CCD: FetchCart error', err); }
    }

    async changeQty(line, qty) {
        const rows = this.querySelectorAll('.ccd-item');
        if (rows[line - 1]) rows[line - 1].style.opacity = '0.5';

        // Track removals for Free Gifts
        if (qty === 0 && this.lastCart && this.lastCart.items[line - 1]) {
            const variantId = this.lastCart.items[line - 1].variant_id;
            // Check if this variant belongs to any active request
            this.removedVariantIds.add(variantId);
            console.log('CCD: User removed item', variantId);
        }

        try {
            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line: line, quantity: qty })
            });
            this.fetchCart();
        } catch (e) {
            console.error('CCD: Qty change error', e);
            if (rows[line - 1]) rows[line - 1].style.opacity = '1';
        }
    }

    updateProgress(cart) {
        if (!cart) return; // Safety check
        const totalPrice = cart.total_price;

        if (!this.offers || this.offers.length === 0) return;
        const sortedOffers = this.offers;

        // 1. Calculate Piecewise Progress
        let percentage = 0;
        const segmentSize = 100 / sortedOffers.length;
        let prevThreshold = 0;

        for (let i = 0; i < sortedOffers.length; i++) {
            const currentThreshold = sortedOffers[i].threshold;

            if (totalPrice < currentThreshold) {
                const range = currentThreshold - prevThreshold;
                const spentInSegment = totalPrice - prevThreshold;
                const segmentProgress = (spentInSegment > 0 ? spentInSegment : 0) / range;

                if (segmentProgress < 0) segmentProgress = 0;
                percentage = (i * segmentSize) + (segmentProgress * segmentSize);
                break;
            } else {
                percentage = (i + 1) * segmentSize;
                prevThreshold = currentThreshold; // IMPORTANT: Move threshold for next segment
            }
        }

        // Ensure full fill if passing last threshold
        if (totalPrice >= sortedOffers[sortedOffers.length - 1].threshold) {
            percentage = 100;
        }

        if (percentage > 100) percentage = 100;

        // DEBUG LOG
        // console.log('CCD Progress:', percentage, totalPrice);

        if (this.progressBar) {
            this.progressBar.style.width = `${percentage}%`;
        } else {
            // Try seeking it again if lost (e.g. DOM update issue)
            this.progressBar = this.querySelector('.ccd-progress-fill');
            if (this.progressBar) {
                this.progressBar.style.width = `${percentage}%`;
            }
        }

        // Update Header Title (Count)
        const title = this.querySelector('.ccd-title');
        if (title) {
            title.innerHTML = `Your Cart (<span id="ccd-count">${cart.item_count || 0}</span> item${cart.item_count === 1 ? '' : 's'})`;
        }

        // Update Header Icon Bubble (globally)
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
            bubble.innerText = cart.item_count;
            bubble.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }

        // 2. Update Milestone Icons & Confetti Trigger
        const milestones = this.querySelectorAll('.ccd-milestone');
        let currentUnlockedCount = 0;

        milestones.forEach(m => {
            const threshold = parseInt(m.dataset.threshold);
            const iconDefault = m.querySelector('.icon-default');
            const iconCheck = m.querySelector('.icon-check');

            if (totalPrice >= threshold) {
                m.classList.add('reached');
                if (iconDefault) iconDefault.style.display = 'none';
                if (iconCheck) iconCheck.style.display = 'block';
                currentUnlockedCount++;
            } else {
                m.classList.remove('reached');
                if (iconDefault) iconDefault.style.display = 'block';
                if (iconCheck) iconCheck.style.display = 'none';
            }
        });

        // Handle Confetti / First Load
        if (this.firstLoad) {
            this.lastUnlockedCount = currentUnlockedCount;
            this.firstLoad = false;
        } else {
            // Trigger Confetti if we reached a NEW milestone
            if (currentUnlockedCount > this.lastUnlockedCount) {
                this.fireConfetti();
            }
            this.lastUnlockedCount = currentUnlockedCount;
        }

        // 3. Status Message
        let nextOffer = sortedOffers.find(offer => offer.threshold > totalPrice);
        let message = '';

        if (nextOffer) {
            const amountRemaining = (nextOffer.threshold - totalPrice) / 100;
            const amountText = amountRemaining.toLocaleString('en-IN', { maximumFractionDigits: 2 });
            message = `You are ₹${amountText} away from <strong>${nextOffer.rewardText}</strong>!`;
        } else {
            message = "🎉 You've unlocked all offers!";
        }

        if (this.progressText) this.progressText.innerHTML = message;

        // 4. Auto Add/Remove Logic
        this.manageFreeGifts(sortedOffers, cart);
    }

    async manageFreeGifts(allOffers, cart) {
        if (!allOffers.length) return;
        const totalPrice = cart.total_price;
        const unlockedOffers = allOffers.filter(offer => offer.threshold <= totalPrice);
        const lockedOffers = allOffers.filter(offer => offer.threshold > totalPrice);

        // A. REMOVE Locked Gifts
        // Check for items in cart that are (1) in lockedOfrers AND (2) have _FreeGift property
        let cleanUpNeeded = false;

        for (const offer of lockedOffers) {
            if (offer.type !== 'product' || !offer.variantId) continue;

            const lineItem = cart.items.find(item =>
                item.variant_id == offer.variantId &&
                item.properties && (item.properties['_FreeGift'] === 'true' || item.properties['_FreeGift'] === true)
            );

            if (lineItem) {
                console.log('CCD: Removing unqualified gift', offer.rewardText);
                try {
                    const lineIndex = cart.items.indexOf(lineItem) + 1;
                    await fetch('/cart/change.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ line: lineIndex, quantity: 0 })
                    });
                    cleanUpNeeded = true;

                    // Also remove from removedVariantIds so it can be added again if they requalify
                    // (Though system removal shouldn't have added it there, but good to be safe)
                    if (this.removedVariantIds.has(parseInt(offer.variantId))) {
                        this.removedVariantIds.delete(parseInt(offer.variantId));
                    }
                    if (this.removedVariantIds.has(String(offer.variantId))) {
                        this.removedVariantIds.delete(String(offer.variantId));
                    }

                } catch (e) { console.error('CCD: Gift removal failed', e); }
            }
        }

        if (cleanUpNeeded) {
            this.fetchCart(); // Refresh and stop (add logic will run on next update)
            return;
        }

        // B. ADD Unlocked Gifts
        for (const offer of unlockedOffers) {
            if (offer.type === 'product' && offer.variantId) {

                // If user explicitly removed this MANUALLY, do not re-add
                if (this.removedVariantIds.has(parseInt(offer.variantId)) || this.removedVariantIds.has(String(offer.variantId))) {
                    continue;
                }

                // Check if already in cart
                const inCart = cart.items.some(item => item.variant_id == offer.variantId); // Don't check property here, just presence
                if (!inCart) {
                    console.log('CCD: Auto-adding free gift', offer.rewardText);
                    try {
                        await fetch('/cart/add.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                id: offer.variantId,
                                quantity: 1,
                                properties: { '_FreeGift': 'true' }
                            })
                        });
                        this.fetchCart();
                        return; // One at a time to avoid race
                    } catch (e) { console.error('Auto Add Failed', e); }
                }
            }
        }
    }

    fireConfetti() {
        const container = document.createElement('div');
        container.className = 'ccd-confetti-container';
        container.style.position = 'absolute';
        container.style.top = '0';
        container.style.left = '0';
        container.style.width = '100%';
        container.style.height = '100%';
        container.style.pointerEvents = 'none';
        container.style.zIndex = '999'; // High enough to be over items, but contained
        container.style.overflow = 'hidden';

        // Append to drawer so it's confined to that area
        if (this.drawer) {
            this.drawer.appendChild(container); // Fix for overlay
        } else {
            this.appendChild(container); // Fallback
        }

        // Falling pieces
        for (let i = 0; i < 50; i++) {
            const p = document.createElement('div');
            p.className = 'ccd-confetti-piece';
            p.style.left = Math.random() * 100 + '%';
            p.style.animationDelay = (Math.random() * 2) + 's';
            p.style.backgroundColor = `hsl(${Math.random() * 360}, 100%, 50%)`;
            p.style.width = (Math.random() * 8 + 5) + 'px';
            p.style.height = (Math.random() * 8 + 5) + 'px';
            container.appendChild(p);
        }

        setTimeout(() => { container.remove(); }, 5000);
    }

    initCoupons() {
        const input = this.querySelector('#ccd-coupon-input');
        const applyBtn = this.querySelector('#ccd-apply-coupon');
        const message = this.querySelector('#ccd-coupon-message');
        const toggleBtn = this.querySelector('#ccd-toggle-coupons');
        const listContainer = this.querySelector('#ccd-coupon-list');
        const countLabel = this.querySelector('#ccd-coupon-count');

        // Load Coupon Data
        let coupons = [];
        try {
            const script = this.querySelector('#ccd-coupons-data');
            if (script) coupons = JSON.parse(script.textContent);
            console.log('CCD: Coupons loaded:', coupons);
        } catch (e) { console.error('CCD: Coupon Parse Error', e); }

        if (countLabel) countLabel.innerText = `${coupons.length} coupons available`;

        // Populate List
        if (listContainer) {
            listContainer.innerHTML = '';
            coupons.forEach(c => {
                const row = document.createElement('div');
                row.style.cssText = 'display:flex; justify-content:space-between; margin-bottom:8px; align-items:center; border-bottom:1px solid #eee; padding-bottom:5px;';
                row.innerHTML = `
                    <div>
                       <div style="font-weight:700; color:#000;">${c.code}</div>
                       <div style="font-size:11px; color:#555;">${c.description}</div>
                    </div>
                    <button class="ccd-use-coupon" data-code="${c.code}" style="background:#000; color:#fff; border:none; border-radius:4px; padding:4px 8px; font-size:10px; cursor:pointer;">APPLY</button>
                `;
                listContainer.appendChild(row);
            });

            // Show/Hide Toggle based on count
            if (coupons.length === 0 && toggleBtn) {
                toggleBtn.style.display = 'none';
                if (countLabel) countLabel.innerText = ''; // Clear "0 available" text
            }
        }

        // Event: Toggle List
        if (toggleBtn) {
            toggleBtn.addEventListener('click', (e) => {
                e.preventDefault();
                listContainer.style.display = listContainer.style.display === 'none' ? 'block' : 'none';
            });
        }

        // Event: Use from List
        this.addEventListener('click', (e) => {
            if (e.target.classList.contains('ccd-use-coupon')) {
                e.preventDefault();
                const code = e.target.dataset.code;
                if (input) input.value = code;
                this.applyCoupon(code);
            }
        });

        // Event: Manual Apply
        if (applyBtn) {
            applyBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const code = input.value.trim();
                if (code) this.applyCoupon(code);
            });
        }

        // Restore active coupon
        const activeCode = sessionStorage.getItem('ccd_discount_code');
        if (activeCode && input) {
            input.value = activeCode;
            this.showCouponSuccess(activeCode);
        }
    }

    applyCoupon(code) {
        sessionStorage.setItem('ccd_discount_code', code);
        this.showCouponSuccess(code);
    }

    showCouponSuccess(code) {
        const message = this.querySelector('#ccd-coupon-message');
        const input = this.querySelector('#ccd-coupon-input');
        if (message) {
            message.style.display = 'block';
            message.style.color = '#00b894';
            message.innerHTML = `Discount code <strong>${code}</strong> applied! It will be calculated at checkout.`;
        }
        if (input) {
            input.disabled = true;
            input.style.color = '#00b894';
            input.style.fontWeight = '700';
        }
    }
}

customElements.define('custom-cart-drawer', CustomCartDrawer);


function updateCartCount() {
  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {

      /* 1️⃣ update custom cart title count */
      const customCount = document.getElementById('ccd-count');
      if (customCount) {
        customCount.textContent = cart.item_count;
      }

      /* 2️⃣ update header cart bubble number */
      const bubbleCount = document.querySelector('[data-testid="cart-bubble"]');
      if (bubbleCount) {
        bubbleCount.textContent = cart.item_count;
      }

      /* 3️⃣ show / hide bubble if empty */
    //   const bubbleWrapper = document.querySelector('.cart-bubble');
    //   if (bubbleWrapper) {
    //     if (cart.item_count > 0) {
    //       bubbleWrapper.classList.remove('visually-hidden');
    //     } else {
    //       bubbleWrapper.classList.add('visually-hidden');
    //     }
    //   }

      /* 4️⃣ update accessible hidden text (optional but good) */
      const hiddenText = document.querySelector('#cart-bubble-text .visually-hidden');
      if (hiddenText) {
        hiddenText.textContent = `Cart count: ${cart.item_count}`;
      }

    });
}

/* run once on load */
updateCartCount();

/* auto update every 6 seconds (as you want) */
setInterval(updateCartCount, 6000);

